/**
 * Aggregate token usage across the devtools event stream.
 *
 * The HUD shows a running total since the panel was opened: how many
 * tokens this dashboard has cost so far, separated into prompt /
 * completion / cached so the cache-hit rate is visible at a glance.
 *
 * Pure utility — accepts an event list, returns aggregate counts. The
 * panel calls it with the live event-store snapshot and re-renders
 * whenever a new `stream-usage` event lands.
 */

import type { DevtoolsEvent, TokenUsage } from "./event-store"

export interface UsageAggregate {
  promptTokens: number
  completionTokens: number
  totalTokens: number
  cachedTokens: number
  cacheWriteTokens: number
  /** Number of `stream-usage` events folded into this total. */
  generations: number
  /** Cache hit rate (cachedTokens / promptTokens), or null if no prompt yet. */
  cacheHitRate: number | null
  /** Most-recently-seen model id, for the HUD label. */
  lastModel: string | null
}

const ZERO: UsageAggregate = {
  promptTokens: 0,
  completionTokens: 0,
  totalTokens: 0,
  cachedTokens: 0,
  cacheWriteTokens: 0,
  generations: 0,
  cacheHitRate: null,
  lastModel: null,
}

export function aggregateUsage(events: DevtoolsEvent[]): UsageAggregate {
  let prompt = 0
  let completion = 0
  let total = 0
  let cached = 0
  let cacheWrite = 0
  let generations = 0
  let lastModel: string | null = null

  for (const event of events) {
    if (event.kind !== "stream-usage") continue
    const u: TokenUsage = event.usage
    prompt += u.promptTokens || 0
    completion += u.completionTokens || 0
    total += u.totalTokens || 0
    cached += u.cachedTokens || 0
    cacheWrite += u.cacheWriteTokens || 0
    generations += 1
    if (u.model) lastModel = u.model
  }

  if (generations === 0) return ZERO

  return {
    promptTokens: prompt,
    completionTokens: completion,
    totalTokens: total,
    cachedTokens: cached,
    cacheWriteTokens: cacheWrite,
    generations,
    cacheHitRate: prompt > 0 ? cached / prompt : null,
    lastModel,
  }
}

/**
 * Format an integer with a thin separator for HUD display.
 * `1234567` → "1 234 567" (Swedish convention; matches the rest of
 * Dash's number formatting).
 */
export function formatTokens(n: number): string {
  return Math.round(n)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, " ")
}
